import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
const API_BASE_URL = process.env.REACT_APP_API_URL;

export const fetchApplications = createAsyncThunk(
  'jobApplications/fetchApplications',
  async ({ page, limit, search, status, jobStatus, deleted }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('limit', limit.toString());
      if (search) queryParams.append('job_title', search);
      if (status) queryParams.append('status', status);
      if (jobStatus) queryParams.append('job_status', jobStatus);
      if (deleted !== undefined) queryParams.append('deleted', deleted.toString()); // Add deleted param

      const response = await axios.get(
        `${API_BASE_URL}dashboard/applications?${queryParams.toString()}`
      );
      return {
        items: response.data.items,
        total: response.data.totalItems,
        currentPage: response.data.page,
        pageSize: limit,
        totalPages: Math.ceil(response.data.totalItems / limit),
        itemCount: response.data.items.length
      };
    } catch (error) {
      console.error('Error fetching applications:', error.response?.data?.message || error.message);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateApplication = createAsyncThunk(
  'jobApplications/updateApplication',
  async ({ applicationId, applicationData }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}dashboard/applications/${applicationId}`,
        applicationData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating application:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteApplication = createAsyncThunk(
  'jobApplications/deleteApplication',
  async (applicationId, { rejectWithValue }) => {
    try {
      await axios.delete(
        `${API_BASE_URL}dashboard/applications/${applicationId}`
      );
      return applicationId;
    } catch (error) {
      console.error('Error deleting application:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const exportApplicationsToCsv = createAsyncThunk(
  'jobApplications/exportApplicationsToCsv',
  async ({ jobId, applicationId, jobStatus, jobTitle, status }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (jobId) queryParams.append('job_id', jobId.toString());
      if (applicationId) queryParams.append('application_id', applicationId.toString());
      if (jobStatus) queryParams.append('job_status', jobStatus);
      if (jobTitle) queryParams.append('job_title', encodeURIComponent(jobTitle));
      if (status) queryParams.append('status', status);

      const response = await axios.get(
        `${API_BASE_URL}dashboard/applications/export/csv${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      );
      return response.data.csv;
    } catch (error) {
      console.error('Error exporting applications to CSV:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const exportResumes = createAsyncThunk(
  'jobApplications/exportResumes',
  async ({ jobId, applicationId, jobStatus, jobTitle, status }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (jobId) queryParams.append('job_id', jobId.toString());
      if (applicationId) queryParams.append('application_id', applicationId.toString());
      if (jobStatus) queryParams.append('job_status', jobStatus);
      if (jobTitle) queryParams.append('job_title', jobTitle);
      if (status) queryParams.append('status', status);

      const response = await axios.get(
        `${API_BASE_URL}dashboard/applications/export/resumes${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
        {
          responseType: 'arraybuffer', // Important for binary data
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error exporting resumes:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const jobsManagementSlice = createSlice({
  name: 'jobApplications',
  initialState: {
    applications: [],
    total: 0,
    loading: false,
    error: null,
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    itemCount: 0,
    success: false
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Applications
      .addCase(fetchApplications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.loading = false;
        state.applications = action.payload.items;
        state.total = action.payload.total;
        state.currentPage = action.payload.currentPage;
        state.pageSize = action.payload.pageSize;
        state.totalPages = action.payload.totalPages;
        state.itemCount = action.payload.itemCount;
      })
      .addCase(fetchApplications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Application
      .addCase(updateApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateApplication.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const index = state.applications.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.applications[index] = action.payload;
        }
      })
      .addCase(updateApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Application
      .addCase(deleteApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteApplication.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.applications = state.applications.filter(
          (application) => application.id !== action.payload
        );
        state.total -= 1;
        state.totalPages = Math.ceil(state.total / state.pageSize);
        if (state.currentPage > state.totalPages && state.totalPages > 0) {
          state.currentPage = state.totalPages;
        }
      })
      .addCase(deleteApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Export to CSV
      .addCase(exportApplicationsToCsv.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(exportApplicationsToCsv.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(exportApplicationsToCsv.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(exportResumes.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(exportResumes.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(exportResumes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default jobsManagementSlice.reducer;